import { useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CustomizationFlagQueries } from '@frontend/api-customization-flags';
import { useHasAutoRecallAnalyticsFeature, useIsPAEligibleAccount, useShowCallIntelNav } from '@frontend/analytics';
import { useHasAppointmentAnalyticsFeature } from '@frontend/analytics-appointment';
import { MarketingAttributionsFeatureFlags } from '@frontend/analytics-marketing-attribution';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAppScopeStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useAnalyticsNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasCallIntelAccess = useShowCallIntelNav();
  const hasAppointAnalyticsFeature = useHasAppointmentAnalyticsFeature();
  const hasAutoRecallAnalyticsFeature = useHasAutoRecallAnalyticsFeature();
  const { isPAEligibleAccount } = useIsPAEligibleAccount();
  const { selectedLocationIds } = useAppScopeStore();
  const { isFeatureHiddenInAllLocations: hideReviewsPage } =
    CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
      locationIds: selectedLocationIds,
      enabled: true,
      customizationFlag: Feature.REVIEWS_HOME_ICON,
    });
  const showMarketingAttribution = getFeatureFlagValue(MarketingAttributionsFeatureFlags.enabled);

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          label: t('Overview'),
          relativePath: '/dashboard',
          trackingId: 'nav-analytics-overview',
          description:
            'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.',
        },
        {
          label: t('Practice'),
          relativePath: '/practice/main',
          show: isPAEligibleAccount,
          trackingId: 'nav-analytics-practice',
          description:
            'In-depth metrics about the practice’s overall performance, including opportunities, patient provider production, and more.',
        },
        {
          label: t('Call Intelligence'),
          relativePath: '/call-intelligence/main',
          show: hasCallIntelAccess,
          trackingId: 'nav-analytics-call-intelligence',
          description: 'Detailed data on call performance, sentiment, call durations, and other telephony insights.',
        },
        {
          label: t('Marketing'),
          relativePath: '/marketing-attribution/main',
          show: showMarketingAttribution,
          trackingId: 'nav-analytics-marketing',
          description: 'Detailed data on marketing performance, attribution, ROI, and other insights.',
        },
        {
          label: t('Auto Recall'),
          relativePath: '/auto-recall',
          show: hasAutoRecallAnalyticsFeature,
          trackingId: 'nav-analytics-auto-recall',
          description:
            'Metrics related to the automated recall system, tracking outreach effectiveness and follow-up responses.',
        },
        {
          label: t('Phone'),
          relativePath: '/phone/main',
          trackingId: 'nav-analytics-phone',
          description: 'Analytics focused on phone usage patterns, volume, and trends for better resource management.',
        },
        {
          label: t('Messaging'),
          relativePath: '/messaging',
          trackingId: 'nav-analytics-messaging',
          description: 'Reports on message volume, response rates, and overall engagement via text or chat channels.',
        },
        {
          label: t('Appointment'),
          relativePath: '/appointment',
          show: hasAppointAnalyticsFeature,
          trackingId: 'nav-analytics-appointments',
          description: 'Tracks appointment-related data, such as scheduling trends, cancellations, and no-show rates.',
        },
        {
          label: t('Reviews'),
          relativePath: '/reviews',
          show: !hideReviewsPage,
          trackingId: 'nav-analytics-reviews',
          description:
            'Tracks patient or customer sentiment over time, analyzing both quantity and quality of reviews.',
        },
      ]),
    [
      t,
      isPAEligibleAccount,
      hasCallIntelAccess,
      showMarketingAttribution,
      hasAutoRecallAnalyticsFeature,
      hasAppointAnalyticsFeature,
      hideReviewsPage,
    ]
  );

  return useMemo(
    () =>
      createNavItem('analytics', {
        iconName: 'analytics',
        label: t('Analytics'),
        root: '/analytics',
        subNavItems,
      }),
    [t, subNavItems]
  );
};
